import { cn } from "@/utils";
import React, { useCallback } from "react";

interface Props extends React.RefAttributes<HTMLInputElement> {
  name?: string;
  label?: string;
  value: string;
  maxLength?: number;
  required?: boolean;
  error?: string;
  placeholder?: string;
  labelBgClass?: string;
  withAsterisk?: boolean;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  allowHyphen?: boolean;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
}

export const NumberString: React.FC<Props> = ({
  value,
  placeholder,
  maxLength,
  required,
  error,
  name,
  labelBgClass,
  withAsterisk,
  disabled,
  label,
  className,
  containerClassName,
  allowHyphen,
  onChange,
  onBlur,
}) => {
  const normalizeFullwidth = useCallback((str: string): string => {
    const fullwidthMap: { [key: string]: string } = {
      "０": "0",
      "１": "1",
      "２": "2",
      "３": "3",
      "４": "4",
      "５": "5",
      "６": "6",
      "７": "7",
      "８": "8",
      "９": "9",
      "－": "-",
      "．": ".",
      "，": ",",
      "　": " ",
    };

    if (allowHyphen) {
      return str
        ?.replace(/[０-９－．， ]/g, (char) => fullwidthMap[char] || char)
        .replace(/[^\d-]/g, "");
    }

    return str
      ?.replace(/[０-９－．，　]/g, (char) => fullwidthMap[char] || char)
      .replace(/\D/g, "");
  }, []);

  const baseInputClasses = cn(
    "h-11 px-[0.88rem] font-normal text-base leading-6 w-full placeholder:text-gray-400/80 placeholder:text-sm border border-gray-300 rounded-md focus:outline-none",
    className,
    disabled ? "bg-grey-200 cursor-not-allowed hover:border-gray-300" : "",
    error
      ? "!border-error_main focus:!border-error_main focus:!ring-error_main"
      : ""
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const originalValue = e.target.value;
      if (!!maxLength && originalValue?.length > maxLength) {
        return;
      }
      const normalizedValue = normalizeFullwidth(originalValue);

      if (normalizedValue !== originalValue) {
        onChange(normalizedValue);
        onBlur?.(normalizedValue);
      }
    },
    [onBlur, normalizeFullwidth]
  );

  return (
    <div style={{ position: "relative" }} className={containerClassName}>
      {label && (
        <label
          className={cn(
            "absolute left-4 top-0 -translate-y-1/2 px-1 text-xs leading-3 z-[2]",
            error ? "text-error_main" : "text-grey-600",
            labelBgClass || ""
          )}
          style={{
            background: disabled
              ? labelBgClass ??
                "linear-gradient(to bottom, #ffffff 50%, #F4F6F8 50%)"
              : labelBgClass ?? "#ffffff",
          }}
        >
          {label}
          {(required || withAsterisk) && (
            <span className="text-error_main ml-1">*</span>
          )}
        </label>
      )}
      <input
        type="text"
        inputMode="tel"
        value={value}
        onChange={(e) => {
          if (!!maxLength && e.target.value.length > maxLength) {
            return;
          }
          onChange(e.target.value);
        }}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        required={required}
        disabled={disabled}
        name={name}
        className={baseInputClasses}
      />

      {error && <p className="text-error_main text-xs mt-1">{error}</p>}
    </div>
  );
};
